import { useState } from "react";
import { useAuth } from "../lib/auth";
import { setLocalConsentVersion } from "../lib/legalConsent";
import { LEGAL_CONSENT_VERSION } from "../../../shared/legalConsent";

/**
 * Aktiver Zustimmungs-Schritt zu AGB + Datenschutz im Web. Blockiert die App,
 * solange die akzeptierte Consent-Version nicht der aktuellen entspricht
 * (Re-Consent bei Versionsbump). Die Volltexte werden in einem neuen Tab
 * geöffnet (öffentliche Routen /agb und /datenschutz).
 */
export default function LegalConsentGate({ onAccepted }: { onAccepted?: () => void }) {
  const { recordConsent } = useAuth();
  const [agbChecked, setAgbChecked] = useState(false);
  const [datenschutzChecked, setDatenschutzChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bothChecked = agbChecked && datenschutzChecked;

  async function handleAccept() {
    if (!bothChecked || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await recordConsent(LEGAL_CONSENT_VERSION);
      setLocalConsentVersion(LEGAL_CONSENT_VERSION);
      onAccepted?.();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Die Zustimmung konnte nicht gespeichert werden.",
      );
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-lg card p-6 space-y-5">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Zustimmung erforderlich</h1>
          <p className="mt-1 text-sm text-slate-600">
            Bitte stimme den folgenden Dokumenten zu, um die App zu nutzen. Du kannst die
            Volltexte vor der Zustimmung lesen.
          </p>
        </div>

        <div className="space-y-3">
          <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-3">
            <input
              type="checkbox"
              checked={agbChecked}
              onChange={(e) => setAgbChecked(e.target.checked)}
              className="mt-1"
              data-testid="checkbox-consent-agb"
            />
            <span className="text-sm text-slate-700">
              Ich stimme den{" "}
              <a
                href="/agb"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-600 hover:underline"
              >
                Allgemeinen Geschäftsbedingungen (AGB)
              </a>{" "}
              zu.
            </span>
          </label>

          <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-3">
            <input
              type="checkbox"
              checked={datenschutzChecked}
              onChange={(e) => setDatenschutzChecked(e.target.checked)}
              className="mt-1"
              data-testid="checkbox-consent-datenschutz"
            />
            <span className="text-sm text-slate-700">
              Ich habe die{" "}
              <a
                href="/datenschutz"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-600 hover:underline"
              >
                Datenschutzerklärung
              </a>{" "}
              gelesen und stimme ihr zu.
            </span>
          </label>
        </div>

        {error ? (
          <p className="text-sm text-red-600" data-testid="text-consent-error">
            {error}
          </p>
        ) : null}

        <button
          type="button"
          onClick={handleAccept}
          disabled={!bothChecked || submitting}
          className="btn-primary w-full disabled:opacity-50"
          data-testid="button-consent-accept"
        >
          {submitting ? "Wird gespeichert …" : "Zustimmen und fortfahren"}
        </button>
      </div>
    </div>
  );
}
